import IosShareIcon from '@mui/icons-material/IosShare';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { Box, Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { getOrInitTwilioClient } from 'app/DesktopLayout/twilioClient';
import BoatImage from "assets/images/boat.png";
import { baseWebUrl } from "lib/constants";
import { useDeleteAvailabilityMutation } from "lib/features/availability/availabilityService";
import { Availability } from "lib/features/availability/types";
import { FareharborAvailability, Item } from "lib/features/item/types";
import {
  selectCurrentChannelUrl,
  selectIsExistingBookingLink,
  setIsExistingBookingLink,
} from "lib/features/message/messageSlice";
import { CreateReferralDto } from "lib/features/referral/referralService";
import { selectUser } from "lib/features/user/userSlice";
import moment from "moment";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import chatServices from 'services/chat-service';
import referralServices from "services/referral-service";
import { availabilityScreenStyles } from "styles/AvailabilityDirectory/availabilityScreen.styles";
import { formatDurationTime } from "util/commonHelper";
import { parseImageUrl } from "util/parseImageUrl";
import BinIcon from "../../../assets/icons/delete_referral.svg";
import DeleteAvailabilityModal from "./DeleteAvailabilityModal";
import EditAvailabilityModal from "./EditAvailabilityModal";
import ShareAvailabilityModal from "./ShareAvailabilityModal";
interface AvailabilityProps {
  completeItem: Item;
  removeItem: (itemId: number) => void;
}

const AvailabilityCard = ({ completeItem, removeItem }: AvailabilityProps) => {
  console.log("selected item: ", completeItem);
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const isExistingBookingLink = useSelector(selectIsExistingBookingLink);
  const currentChannelUrl = useSelector(selectCurrentChannelUrl);

  const fallbackImage =
    parseImageUrl(completeItem?.itemImages?.[0]?.uri) || BoatImage;
  const [id, setId] = useState<number>();
  const [isDeleteModal, setIsDeleteModal] = useState<boolean>(false);
  const [isEditModal, setIsEditModal] = useState<boolean>(false);
  const [isShareModal, setIsShareModal] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isConfirmShareModal, setIsConfirmShareModal] = useState<boolean>(false);

  const [item, setItem] = useState<Item>(completeItem);
  const [availability, setAvailability] = useState<Availability | FareharborAvailability>();

  const [deleteAvailability, { isLoading: deleteLoading }] =
    useDeleteAvailabilityMutation();


  const handleDeleteAvailability = async () => {
    if (!id) return;
    try {
      await deleteAvailability(id).unwrap();
      const updatedAvailabilities = item?.availabilities?.filter(
        (item) => item?.id !== id
      );
      if (updatedAvailabilities.length === 0) {
        removeItem(item?.id);
      } else {
        setItem({ ...item, availabilities: updatedAvailabilities });
      }
      setIsDeleteModal(false);
    } catch (error) {
      console.log("error: ", error);
    }
  };


  const calculateDuration = (startTime: Date, endTime: Date): number => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    let timeDifference = (end.getTime() - start.getTime()) / (1000 * 3600); // Difference in hours

    if (timeDifference < 0) {
      // Adjust the end time if the time difference is negative (crosses midnight)
      const correctedEndTime = new Date(end);
      correctedEndTime.setDate(correctedEndTime.getDate() + 1); // Move to the next day
      return calculateDuration(startTime, correctedEndTime);
    }

    if (timeDifference > 12) {
      timeDifference -= 12;
    }

    return timeDifference;
  };

  const handleShare = async (selectedAvailability: Availability | FareharborAvailability) => {
    if (!user?.id || !selectedAvailability || !currentChannelUrl) {
      console.error("Missing required data: user, availability, or channel URL");
      return;
    }

    try {
      setLoading(true);

      let referralPayload: CreateReferralDto;

      // Internal availability (non-FareHarbor)
      if (item.item_type !== 4) {
        const internalAvailability = selectedAvailability as Availability;

        referralPayload = {
          user_id: user.id,
          business_id: item.business.id,
          availability_id: internalAvailability.id,
          item_id: item.id,
          referral_type: 1,
          state: 1,
        };
      } else {
        // FareHarbor availability
        const fhAvailability = selectedAvailability as FareharborAvailability;

        referralPayload = {
          user_id: user.id,
          business_id: item.business.id,
          referral_type: 4,
          external_availability_id: fhAvailability.pk,
          fareharbor_customer_prototype_id:
            fhAvailability.customer_type_rates?.[0]?.pk,
          item_id: item.id,
          state: 1,
        };
      }

      const referralResponse = await referralServices.createReferral(referralPayload);
      const referralId = referralResponse?.data?.id;

      if (!referralId) {
        throw new Error("Missing referral id from createReferral response");
      }

      const url = `${baseWebUrl}/checkout/${referralId}`;

      // Initialize Twilio client if needed and get the conversation
      const fetchToken = async () => {
        const res = await chatServices.getUserTokenForChat(String(user.id));
        return String(res.data.token);
      };

      const client = await getOrInitTwilioClient(fetchToken);
      // currentChannelUrl may contain full path: {sid}/{avatar}/{name}/{myavatar}
      // Extract just the SID for the API call
      const conversationSid = currentChannelUrl.split('/')[0];
      const conversation = await client.getConversationBySid(conversationSid);

      // Send the message directly via Twilio SDK
      await conversation.sendMessage(url);
      dispatch(setIsExistingBookingLink(false));
      // Navigate back to the chat
      navigate(`/chat/${currentChannelUrl}`);
    } catch (error) {
      console.error("Error sending message to conversation:", error);
    } finally {
      setLoading(false);
    }
  };

  const refereeName = (() => {
    const name = decodeURIComponent(currentChannelUrl)
      ?.split("/")?.[2];

    const parts = name?.split(" ") ?? [];

    return parts.length > 1
      ? `${parts[0]} ${parts[1][0]}.`
      : parts[0];
  })()
  return (
    <Box sx={availabilityScreenStyles.card(theme)}>
      {/* Image Box */}
      <Box sx={availabilityScreenStyles.imageBox}>
        <img
          src={fallbackImage}
          alt={"image"}
          style={availabilityScreenStyles.image}
        />
      </Box>

      <Box maxHeight="262px" p={2}>
        {/* Body */}
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={availabilityScreenStyles.titleText}>
              {item?.itemDetails[0]?.item_name}
            </Typography>
            <Box sx={availabilityScreenStyles.availabilityBadge}>
              {item?.availabilities.map((data: Availability) => (
                <Typography style={availabilityScreenStyles.availabilityText}>
                  {data?.quantity} Available
                </Typography>
              ))}

            </Box>
          </Box>
          <Typography
            sx={[availabilityScreenStyles.subtitleText, { paddingTop: "2px" }]}
          >
            {item?.itemDetails[0]?.headline}
          </Typography>
        </Box>

        {/* Details Section */}
        {item?.availabilities.map((data: Availability) => (
          <>
            <Box key={data.id} sx={availabilityScreenStyles.detailContainer}>
              {/* First Row */}
              <Box sx={availabilityScreenStyles.rowContainer}>
                {/* Time and Duration section */}
                <Box
                  sx={availabilityScreenStyles.timeContainer}
                  onClick={() => {
                    setId(item.id);
                    setAvailability(data);
                  }}
                >
                  <Typography sx={availabilityScreenStyles.rowText}>
                    {moment
                      .utc(data?.start_time)
                      .tz("America/New_York")
                      .format("hh:mm A")}
                  </Typography>

                  {item &&
                    item.itemDetails[0].duration_applicable &&
                    item.itemDetails[0].duration && (
                      <>
                        <Typography
                          sx={[
                            availabilityScreenStyles.rowText,
                            availabilityScreenStyles.dotText,
                          ]}
                        >
                          •
                        </Typography>

                        <Typography sx={availabilityScreenStyles.rowText}>
                          {formatDurationTime(item.itemDetails[0].duration) + " hr"}
                        </Typography>
                      </>
                    )}

                </Box>
                <Box sx={availabilityScreenStyles.priceSection}>
                  <Typography sx={availabilityScreenStyles.priceLabel}>
                    Price:
                  </Typography>
                  <Typography sx={availabilityScreenStyles.priceValue}>
                    ${item?.itemDetails[0]?.price}
                  </Typography>
                </Box>

                <Box sx={availabilityScreenStyles.priceSection}>
                  <Typography sx={availabilityScreenStyles.priceLabel}>
                    Pays:
                  </Typography>
                  <Typography sx={availabilityScreenStyles.priceValue}>
                    {`${item?.itemDetails[0]?.comission}%`} (
                    {`$${(item?.itemDetails[0]?.price *
                      (item?.itemDetails[0]?.comission || 0)) /
                      100
                      }`}
                    )
                  </Typography>
                </Box>

                {
                  item.availabilities?.[0].private && (
                    <Box sx={availabilityScreenStyles.priceSection}>
                      <Typography sx={availabilityScreenStyles.priceLabel}>
                        Referee :
                      </Typography>
                      <Typography sx={availabilityScreenStyles.priceValue}>
                        {
                          item.availabilities?.[0]?.referrals?.[0]?.business?.business
                            ? item.availabilities?.[0]?.referrals?.[0]?.business?.business
                            : (item.availabilities?.[0]?.referrals?.[0]?.user?.first_name && item.availabilities?.[0]?.referrals?.[0]?.user?.last_name)
                              ? `${item.availabilities?.[0]?.referrals?.[0]?.user?.first_name} ${item.availabilities?.[0]?.referrals?.[0]?.user?.last_name}`
                              : 'Unknown'
                        }
                      </Typography>
                    </Box>
                  )
                }
                {
                  currentChannelUrl && !item.availabilities?.[0].private && isExistingBookingLink && (
                    <Box sx={availabilityScreenStyles.priceSection}>
                      <Typography sx={availabilityScreenStyles.priceLabel}>
                        Referee :
                      </Typography>
                      <Typography sx={availabilityScreenStyles.priceValue}>
                        {refereeName}
                      </Typography>
                    </Box>
                  )
                }
              </Box>


            </Box>
            <Box height="84px" display="flex" justifyContent="center" gap={2} alignItems="center" >
              {
                !item?.availabilities?.[0].private && (
                  <>
                    {
                      loading ? (
                        <Button variant="contained" disabled sx={{ width: "174px", height: "44px", "&.Mui-disabled": { backgroundColor: "#235e24" } }}>
                          <CircularProgress size={20} sx={{ color: "#fff" }} />
                        </Button>
                      ) : (
                        <Button variant="contained" sx={{ width: "174px", height: "44px" }} startIcon={<IosShareIcon />} onClick={() => {
                          setAvailability(data);
                          if (isExistingBookingLink) {
                            handleShare(data);
                            setLoading(true);
                          } else {
                            setIsShareModal(true);
                          }
                        }}>
                          Share
                        </Button>
                      )
                    }
                  </>
                )
              }
              {
                !isExistingBookingLink && (
                  <Button variant="outlined" color="inherit" sx={{ width: "174px", height: "44px" }} startIcon={<ModeEditIcon />}
                    onClick={() => {
                      setId(item.id);
                      setIsEditModal(true);
                      setAvailability(data);
                    }}>
                    Edit
                  </Button>
                )
              }
              {
                !isExistingBookingLink && (
                  <Button variant="outlined" color="inherit" sx={{ width: "174px", height: "44px" }} startIcon={<img src={BinIcon} width={20} height={20} />} onClick={() => {
                    setId(data.id);
                    setIsDeleteModal(true);
                  }}>
                    Delete
                  </Button>
                )
              }

            </Box>
          </>
        ))}
      </Box>

      {/* Delete Modal */}
      <DeleteAvailabilityModal
        open={isDeleteModal}
        close={() => setIsDeleteModal(false)}
        loading={deleteLoading}
        onSubmit={handleDeleteAvailability}
      />

      {/* Edit Modal */}
      {availability && (
        <EditAvailabilityModal
          open={isEditModal}
          close={() => setIsEditModal(false)}
          selectedId={id}
          selectedAvailability={availability as Availability}
          item={item}
          setItem={setItem}
        />
      )}

      {availability && (
        <ShareAvailabilityModal
          open={isShareModal}
          close={() => setIsShareModal(false)}
          selectedAvailability={availability}
          item={item}
        />
      )}
    </Box>
  );
};

export default AvailabilityCard;
